import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";
import { Event, Media, People, Post, Tool } from "../types/content";
import { Topic } from "../types/category";

// Define a type for the slice state
export interface ContentState {
  collections: {
    data: any[];
    waiting: boolean;
    loading: boolean;
    errors: any[];
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  },
  collection: {
    data: any | null;
    waiting: boolean;
    loading: boolean;
    errors: any[];
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  };
  posts: {
    data: Post[];
    waiting: boolean;
    loading: boolean;
    errors: any[];
    /**
     * Which selection `data` currently holds — "featured", "new", or a
     * tag/topic key.
     *
     * getFeaturedContent, getNewContent and getAllContentWithTag all write to this
     * one bucket, and whichever response landed last used to win. On the landing
     * page the auth-ready effect fires getFeaturedContent while the user may
     * already have clicked "New", so a late featured response overwrote the new
     * content — and because nothing is flagged featured, it overwrote it with an
     * empty list, leaving "New" highlighted above no results.
     */
    source: string | null;
    options: { [key: string]: Array<string[]> };
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  },
  post: {
    data: Post | null;
    waiting: boolean;
    loading: boolean;
    errors: any[];
  };
  events: {
    data: Event[];
    waiting: boolean;
    loading: boolean;
    errors: any[];
    options: { [key: string]: Array<string[]> };
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  },
  event: {
    data: Event | null;
    waiting: boolean;
    loading: boolean;
    errors: any[];
  };
  peoples: {
    data: People[];
    waiting: boolean;
    loading: boolean;
    errors: any[];
    options: { [key: string]: Array<string[]> };
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  },
  people: {
    data: People | null;
    waiting: boolean;
    loading: boolean;
    errors: any[];
  };
  medias: {
    data: Media[];
    waiting: boolean;
    loading: boolean;
    errors: any[];
    options: { [key: string]: Array<string[]> };
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  },
  media: {
    data: Media | null;
    waiting: boolean;
    loading: boolean;
    errors: any[];
  };
  tools: {
    data: Tool[];
    waiting: boolean;
    loading: boolean;
    errors: any[];
    options: { [key: string]: Array<string[]> };
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  },
  tool: {
    data: Tool | null;
    waiting: boolean;
    loading: boolean;
    errors: any[];
  };
  supports: {
    data: any[];
    waiting: boolean;
    loading: boolean;
    errors: any[];
    options: { [key: string]: Array<string[]> };
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  },
  search: {
    data: any[];
    waiting: boolean;
    loading: boolean;
    errors: any[];
    /**
     * Identifies which filter combination `data` holds.
     *
     * Results paginate by appending ("Show more"), and the decision to append used
     * to be `pageNumber !== 1` alone. A filter change that kept the page number in
     * the URL therefore appended the new filter's page 2 onto the old filter's
     * page 1, showing two different searches stacked together. Comparing the
     * signature makes appending conditional on the filters actually matching.
     */
    signature: string | null;
    options: { [key: string]: Array<string[]> };
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  },
  tags: {
    data: any[];
    waiting: boolean;
    loading: boolean;
    errors: any[];
    options: { [key: string]: Array<string[]> };
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  },
  topic: {
    data: Topic | null;
    waiting: boolean;
    loading: boolean;
    errors: any[];
  }
}

// Define the initial state using that type
const initialState: ContentState = {
  collections: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    },
  },
  collection: {
    data: null,
    waiting: false,
    loading: false,
    errors: [],
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    },
  },
  posts: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    source: null,
    options: {},
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    },
  },
  post: {
    data: null,
    waiting: false,
    loading: false,
    errors: [],
  },
  events: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    options: {},
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    },
  },
  event: {
    data: null,
    waiting: false,
    loading: false,
    errors: [],
  },
  peoples: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    options: {},
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    },
  },
  people: {
    data: null,
    waiting: false,
    loading: false,
    errors: [],
  },
  medias: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    options: {},
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    },
  },
  media: {
    data: null,
    waiting: false,
    loading: false,
    errors: [],
  },
  tools: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    options: {},
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    },
  },
  tool: {
    data: null,
    waiting: false,
    loading: false,
    errors: [],
  },
  supports: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    options: {},
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    },
  },
  search: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    signature: null,
    options: {},
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    },
  },
  tags: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    options: {},
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    },
  },
  topic: {
    data: null,
    waiting: false,
    loading: false,
    errors: [],
  }
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get Collections
export const getCollections = createAsyncThunk(
  "content/collection/get",
  async (
    obj: {
      pageNumber: number;
      query: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/collection${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get Collection by Slug
export const getCollectionBySlug = createAsyncThunk(
  "content/collection/slug/get",
  async (
    obj: {
      slug: string | undefined;
      pageNumber: number;
      query: string | null;
      contentType: string | null;
      consumption: string | null;
      audience: string | null;
      profileId: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {

      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      if (obj.contentType !== null && obj.contentType !== "") params.type = obj.contentType;
      if (obj.consumption !== null && obj.consumption !== "") params.consumption = obj.consumption;
      if (obj.audience !== null && obj.audience !== "") params.audience = obj.audience;
      if (obj.profileId !== null) params.profile_id = obj.profileId;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/collection/${obj.slug}${searchParams === "" ? "" : "?" + searchParams}`;

      // Configure authorization header with user's token. The token has to travel
      // on this request even though the endpoint is public: a members-only
      // collection is unlocked by who is asking, and without the header the API
      // sees an anonymous visitor and withholds the curated items from the member
      // who is signed in and looking right at them.
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get Posts
export const getPosts = createAsyncThunk(
  "content/post/get",
  async (
    obj: {
      pageNumber: number;
      query: string | null;
      tag: string | null;
      topic: string | null;
      interest: string | null;
      audience: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      if (obj.tag !== null && obj.tag !== "") params.tag = obj.tag;
      if (obj.topic !== null && obj.topic !== "") params.topic = obj.topic;
      if (obj.interest !== null && obj.interest !== "") params.interest = obj.interest;
      if (obj.audience !== null && obj.audience !== "") params.audience = obj.audience;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/post${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get Post by Slug
export const getPostBySlug = createAsyncThunk(
  "content/post/slug/get",
  async (
    obj: {
      slug: string | undefined;
    },
    { getState, rejectWithValue }
  ) => {
    try {

      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      if (auth.user !== null) params.profile_id = auth.user.profile.id.toString();
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/post/${obj.slug}${searchParams === "" ? "" : "?" + searchParams}`;

      // Members-only posts are unlocked by who is asking, so the token travels even
      // though the endpoint is public. `profile_id` above identifies the viewer for
      // view-logging only -- the API will not gate on it.
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get Events
export const getEvents = createAsyncThunk(
  "content/event/get",
  async (
    obj: {
      pageNumber: number;
      query?: string | null;
      category: string | null;
      tag: string | null;
      audience: string | null;
      location: string | null;
      type: string | null;
      startDate: string | null;
      endDate: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query) params.query = obj.query;
      if (obj.category !== null && obj.category !== "") params.category = obj.category;
      if (obj.tag !== null && obj.tag !== "") params.tag = obj.tag;
      if (obj.audience !== null && obj.audience !== "") params.audience = obj.audience;
      if (obj.location !== null && obj.location !== "") params.location = obj.location;
      if (obj.type !== null && obj.type !== "") params.type = obj.type;
      if (obj.startDate !== null && obj.startDate !== "") params.start_date = obj.startDate;
      if (obj.endDate !== null && obj.endDate !== "") params.end_date = obj.endDate;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/event${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get Event Locations
export const getEventLocations = createAsyncThunk(
  "content/event/locations/get",
  async (
    args,
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const url = `/api/content/event/location`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get Event by Slug
export const getEventBySlug = createAsyncThunk(
  "content/event/slug/get",
  async (
    obj: {
      slug: string | undefined;
    },
    { getState, rejectWithValue }
  ) => {
    try {

      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      if (auth.user !== null) params.profile_id = auth.user.profile.id.toString();
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/event/${obj.slug}${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get People
export const getPeoples = createAsyncThunk(
  "content/people/get",
  async (
    obj: {
      pageNumber: number;
      query: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/people${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get People by Slug
export const getPeopleBySlug = createAsyncThunk(
  "content/people/slug/get",
  async (
    obj: {
      slug: string | undefined;
    },
    { getState, rejectWithValue }
  ) => {
    try {

      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      if (auth.user !== null) params.profile_id = auth.user.profile.id.toString();
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/people/${obj.slug}${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get Tools
export const getTools = createAsyncThunk(
  "content/tool/get",
  async (
    obj: {
      pageNumber: number;
      query: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/tool${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get Tool by Slug
export const getToolBySlug = createAsyncThunk(
  "content/tool/slug/get",
  async (
    obj: {
      slug: string | undefined;
    },
    { getState, rejectWithValue }
  ) => {
    try {

      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      if (auth.user !== null) params.profile_id = auth.user.profile.id.toString();
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/tool/${obj.slug}${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get Media
export const getMedia = createAsyncThunk(
  "content/media/get",
  async (
    obj: {
      pageNumber: number;
      query: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/media${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get Media by Slug
export const getMediaBySlug = createAsyncThunk(
  "content/media/slug/get",
  async (
    obj: {
      slug: string | undefined;
    },
    { getState, rejectWithValue }
  ) => {
    try {

      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      if (auth.user !== null) params.profile_id = auth.user.profile.id.toString();
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/media/${obj.slug}${searchParams === "" ? "" : "?" + searchParams}`;

      // As with posts: the token unlocks members-only media, `profile_id` does not.
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Search all content
export const searchContent = createAsyncThunk(
  "content/search",
  async (
    obj: {
      pageNumber: number;
      query: string | null;
      contentType: string | null;
      consumption: string | null;
      topic: string | null;
      tag: string | null;
      audience: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      if (obj.contentType !== null && obj.contentType !== "") params.type = obj.contentType;
      if (obj.consumption !== null && obj.consumption !== "") params.consumption = obj.consumption;
      if (obj.topic !== null && obj.topic !== "") params.topic = obj.topic;
      if (obj.tag !== null && obj.tag !== "") params.tag = obj.tag;
      if (obj.audience !== null && obj.audience !== "") params.audience = obj.audience;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/search${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Mark content as viewed
export const markContentAsViewed = createAsyncThunk(
  "content/mark/view",
  async (
    obj: {
      id: string;
      type: string;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const headers = {
        Authorization: `Bearer ${auth.access}`,
        "Content-Type": "application/json",
      };

      const body = JSON.stringify({
        id: obj.id,
        object_type: obj.type
      });

      const res = await axios.post(`/api/log/view`, body, { headers });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get tags
export const getTags = createAsyncThunk(
  "content/tags/get",
  async (
    obj: {
      query: string | null;
      pageNumber: number;
      pageSize: number
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      params.size = obj.pageSize.toString();
      params.page = obj.pageNumber.toString();
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/tag${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get filter tags
export const getFilterTags = createAsyncThunk(
  "content/filter/tags/get",
  async (
    obj: {
      pageNumber: number;
      pageSize: number
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      params.size = obj.pageSize.toString();
      params.page = obj.pageNumber.toString();
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/filter/tag${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get topic tags
export const getTopicTags = createAsyncThunk(
  "content/topic/tags/get",
  async (
    obj: {
      topicSlug: string | undefined;
      pageNumber: number;
      pageSize: number
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      params.topic = obj.topicSlug === undefined ? "" : obj.topicSlug;
      params.size = obj.pageSize.toString();
      params.page = obj.pageNumber.toString();
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/topic/tag${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get featured content
export const getFeaturedContent = createAsyncThunk(
  "content/featured/get",
  async (
    obj: {
      pageNumber: number;
      query: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/featured${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get new content
export const getNewContent = createAsyncThunk(
  "content/new/get",
  async (
    obj: {
      pageNumber: number;
      query: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/new${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get all content with Tag or Topic
export const getAllContentWithTag = createAsyncThunk(
  "content/all-content/tag/get",
  async (
    obj: {
      pageNumber: number;
      tag: string | null;
      /**
       * Topic *slug*. The search endpoint resolves it to the topic name itself,
       * so pass the slug rather than the display name.
       *
       * Filtering by topic is not the same as filtering by a tag that happens to
       * share the topic's name: a Topic groups several tags (see Topic.tags), so
       * the topic filter returns everything under it.
       */
      topic?: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString()
      if (obj.tag !== null && obj.tag !== "") params.tag = obj.tag;
      if (obj.topic !== null && obj.topic !== undefined && obj.topic !== "") params.topic = obj.topic;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/search${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get other content
export const getOtherContent = createAsyncThunk(
  "content/other/get",
  async (
    obj: {
      pageNumber: number;
      contentType: string;
      topic: string | null | undefined;
      collection: string | null | undefined;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      params.type = obj.contentType;
      if (obj.topic !== undefined && obj.topic !== null) params.topic = obj.topic;
      if (obj.collection !== undefined && obj.collection !== null) params.collection = obj.collection;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/other${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get topic metadata
export const getTopicMetadata = createAsyncThunk(
  "content/topic/metadata/get",
  async (
    obj: {
      topicSlug: string | undefined;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.topic = obj.topicSlug === undefined ? "" : obj.topicSlug;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/content/topic/metadata${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Create or Update review rating
export const submitReviewRatingForContent = createAsyncThunk(
  "content/review/create",
  async (
    obj: {
      objectId: string;
      objectType: string;
      score: number;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        }
      };

      // Build endpoint URL
      const url = `/api/review`;

      const body = JSON.stringify({
        id: obj.objectId,
        type: obj.objectType,
        score: obj.score,
      });

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

/**
 * Key identifying which tag/topic selection a getAllContentWithTag call is for.
 *
 * Compared in the reducer so a slow response cannot overwrite a newer selection.
 */
function contentSelectionKey(arg: { tag?: string | null; topic?: string | null }): string {
  return arg.topic ? `topic:${arg.topic}` : `tag:${arg.tag ?? ""}`;
}

/**
 * Identify a search by its filters, ignoring the page number.
 *
 * Two requests share a signature exactly when one can be the next page of the
 * other. See `ContentState["search"].signature`.
 */
function searchFilterSignature(arg: {
  query: string | null;
  contentType: string | null;
  consumption: string | null;
  topic: string | null;
  tag: string | null;
  audience: string | null;
}): string {
  return JSON.stringify([
    arg.query ?? "",
    arg.contentType ?? "",
    arg.consumption ?? "",
    arg.topic ?? "",
    arg.tag ?? "",
    arg.audience ?? "",
  ]);
}

/**
 * Write a freshly computed rating aggregate onto every copy of an object held in
 * the content slice.
 *
 * The same object can sit in several buckets at once (a post is in `posts`, in
 * `search`, and possibly in `post`), and the star widget reads whichever one its
 * card was rendered from — so updating a single bucket leaves the others stale.
 */
function applyRatingAggregate(state: ContentState, objectId: string, data: any) {
  if (typeof data?.rating !== "number" || typeof data?.reviews !== "number") return;

  const buckets: any[] = [
    state.posts.data, state.events.data, state.supports.data,
    state.tools.data, state.medias.data, state.peoples.data,
    state.search.data, state.collections.data,
    // Single-object buckets, wrapped so the same loop covers them.
    state.post.data ? [state.post.data] : [],
    state.event.data ? [state.event.data] : [],
    state.tool.data ? [state.tool.data] : [],
    state.media.data ? [state.media.data] : [],
    state.people.data ? [state.people.data] : [],
  ];

  for (const bucket of buckets) {
    if (!Array.isArray(bucket)) continue;
    for (const item of bucket) {
      if (item && item.id === objectId) {
        item.rating = data.rating;
        item.reviews = data.reviews;
      }
    }
  }
}

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const contentSlice = createSlice({
  name: "content",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get Collections
      .addCase(getCollections.pending, (state) => {
        state.collections.loading = true;
      })
      .addCase(getCollections.fulfilled, (state, action) => {
        state.collections.loading = false;
        state.collections.data = action.payload.data.data;
        state.collections.pagination = action.payload.data.pagination;
      })
      .addCase(getCollections.rejected, (state, action) => {
        state.collections.loading = false;
        state.collections.errors.push(action.payload as string);
      })

      // Get Collection by Slug
      .addCase(getCollectionBySlug.pending, (state, action) => {
        state.collection.loading = true;
      })
      .addCase(getCollectionBySlug.fulfilled, (state, action) => {
        state.collection.loading = false;
        state.collection.data = action.payload.data;
      })
      .addCase(getCollectionBySlug.rejected, (state, action) => {
        state.collection.loading = false;
        state.collection.errors.push(action.payload as string);
      })

      // Get Posts
      .addCase(getPosts.pending, (state) => {
        state.posts.loading = true;
      })
      .addCase(getPosts.fulfilled, (state, action) => {
        state.posts.loading = false;
        state.posts.data = action.payload.data.data;
        state.posts.options = action.payload.data.options;
        state.posts.pagination = action.payload.data.pagination;
      })
      .addCase(getPosts.rejected, (state, action) => {
        state.posts.loading = false;
        state.posts.errors.push(action.payload as string);
      })

      // Get Post by Slug
      .addCase(getPostBySlug.pending, (state) => {
        state.post.loading = true;
      })
      .addCase(getPostBySlug.fulfilled, (state, action) => {
        state.post.loading = false;
        state.post.data = action.payload.data;
      })
      .addCase(getPostBySlug.rejected, (state, action) => {
        state.post.loading = false;
        state.post.errors.push(action.payload as string);
      })

      // Get Events
      .addCase(getEvents.pending, (state) => {
        state.events.loading = true;
      })
      .addCase(getEvents.fulfilled, (state, action) => {
        state.events.loading = false;
        state.events.data = action.payload.data.data;
        // state.events.options = action.payload.data.options;
        state.events.pagination = action.payload.data.pagination;
      })
      .addCase(getEvents.rejected, (state, action) => {
        state.events.loading = false;
        state.events.errors.push(action.payload as string);
      })

      // Get Event Locations
      .addCase(getEventLocations.pending, (state) => {
        state.events.loading = true;
      })
      .addCase(getEventLocations.fulfilled, (state, action) => {
        state.events.loading = false;
        state.events.options.cities = action.payload.data;
      })
      .addCase(getEventLocations.rejected, (state, action) => {
        state.events.loading = false;
        state.events.errors.push(action.payload as string);
      })

      // Get Event by Slug
      .addCase(getEventBySlug.pending, (state) => {
        state.event.loading = true;
      })
      .addCase(getEventBySlug.fulfilled, (state, action) => {
        state.event.loading = false;
        state.event.data = action.payload.data;
      })
      .addCase(getEventBySlug.rejected, (state, action) => {
        state.event.loading = false;
        state.event.errors.push(action.payload as string);
      })

      // Get People
      .addCase(getPeoples.pending, (state) => {
        state.peoples.loading = true;
      })
      .addCase(getPeoples.fulfilled, (state, action) => {
        state.peoples.loading = false;
        state.peoples.data = action.payload.data.data;
        state.peoples.options = action.payload.data.options;
        state.peoples.pagination = action.payload.data.pagination;
      })
      .addCase(getPeoples.rejected, (state, action) => {
        state.peoples.loading = false;
        state.peoples.errors.push(action.payload as string);
      })

      // Get People by Slug
      .addCase(getPeopleBySlug.pending, (state) => {
        state.people.loading = true;
      })
      .addCase(getPeopleBySlug.fulfilled, (state, action) => {
        state.people.loading = false;
        state.people.data = action.payload.data;
      })
      .addCase(getPeopleBySlug.rejected, (state, action) => {
        state.people.loading = false;
        state.people.errors.push(action.payload as string);
      })

      // Get Tools
      .addCase(getTools.pending, (state) => {
        state.tools.loading = true;
      })
      .addCase(getTools.fulfilled, (state, action) => {
        state.tools.loading = false;
        state.tools.data = action.payload.data.data;
        state.tools.options = action.payload.data.options;
        state.tools.pagination = action.payload.data.pagination;
      })
      .addCase(getTools.rejected, (state, action) => {
        state.tools.loading = false;
        state.tools.errors.push(action.payload as string);
      })

      // Get Tool by Slug
      .addCase(getToolBySlug.pending, (state) => {
        state.tool.loading = true;
      })
      .addCase(getToolBySlug.fulfilled, (state, action) => {
        state.tool.loading = false;
        state.tool.data = action.payload.data;
      })
      .addCase(getToolBySlug.rejected, (state, action) => {
        state.tool.loading = false;
        state.tool.errors.push(action.payload as string);
      })

      // Get Media
      .addCase(getMedia.pending, (state) => {
        state.medias.loading = true;
      })
      .addCase(getMedia.fulfilled, (state, action) => {
        state.medias.loading = false;
        state.medias.data = action.payload.data.data;
        state.medias.options = action.payload.data.options;
        state.medias.pagination = action.payload.data.pagination;
      })
      .addCase(getMedia.rejected, (state, action) => {
        state.medias.loading = false;
        state.medias.errors.push(action.payload as string);
      })

      // Get Media by Slug
      .addCase(getMediaBySlug.pending, (state) => {
        state.media.loading = true;
      })
      .addCase(getMediaBySlug.fulfilled, (state, action) => {
        state.media.loading = false;
        state.media.data = action.payload.data;
      })
      .addCase(getMediaBySlug.rejected, (state, action) => {
        state.media.loading = false;
        state.media.errors.push(action.payload as string);
      })

      // Search all content
      .addCase(searchContent.pending, (state, action) => {
        const pageNumber = action.meta.arg.pageNumber;
        const signature = searchFilterSignature(action.meta.arg);
        // Only a genuine "Show more" on an unchanged filter set may keep the
        // existing results. Anything else starts a fresh list, so a stale page
        // number in the URL can no longer stack two searches on top of each other.
        const isSamePage = pageNumber !== 1 && state.search.signature === signature;
        if (!isSamePage) {
          state.search.data = [];
          state.search.loading = true;
          state.search.waiting = false;
        } else {
          state.search.waiting = true;
        }
        state.search.signature = signature;
      })
      .addCase(searchContent.fulfilled, (state, action) => {
        const pageNumber = action.meta.arg.pageNumber;
        const signature = searchFilterSignature(action.meta.arg);
        // Append only when this really is the next page of what is on screen.
        // `signature` still matches because pending stamped it; an in-flight
        // request whose filters were superseded loses the comparison and replaces.
        const append = pageNumber !== 1 && state.search.signature === signature && state.search.data.length > 0;
        state.search.loading = false;
        state.search.waiting = false;
        state.search.data = append
          ? [...state.search.data, ...action.payload.data.data]
          : action.payload.data.data;
        state.search.options = action.payload.data.options;
        state.search.pagination = action.payload.data.pagination;
      })
      .addCase(searchContent.rejected, (state, action) => {
        state.search.loading = false;
        state.search.waiting = false;
        state.search.errors.push(action.payload as string);
      })

      // Get tags
      .addCase(getTags.pending, (state) => {
        state.tags.loading = true;
      })
      .addCase(getTags.fulfilled, (state, action) => {
        state.tags.loading = false;
        state.tags.data = action.payload.data.data;
        state.tags.options = action.payload.data.options;
        state.tags.pagination = action.payload.data.pagination;
      })
      .addCase(getTags.rejected, (state, action) => {
        state.tags.loading = false;
        state.tags.errors.push(action.payload as string);
      })

      // Get filter tags
      .addCase(getFilterTags.pending, (state) => {
        state.tags.loading = true;
      })
      .addCase(getFilterTags.fulfilled, (state, action) => {
        state.tags.loading = false;
        state.tags.data = action.payload.data.data;
        state.tags.options = action.payload.data.options;
        state.tags.pagination = action.payload.data.pagination;
      })
      .addCase(getFilterTags.rejected, (state, action) => {
        state.tags.loading = false;
        state.tags.errors.push(action.payload as string);
      })

      // Get topic tags
      .addCase(getTopicTags.pending, (state) => {
        state.tags.loading = true;
      })
      .addCase(getTopicTags.fulfilled, (state, action) => {
        state.tags.loading = false;
        state.tags.data = action.payload.data.data;
        state.tags.options = action.payload.data.options;
        state.tags.pagination = action.payload.data.pagination;
      })
      .addCase(getTopicTags.rejected, (state, action) => {
        state.tags.loading = false;
        state.tags.errors.push(action.payload as string);
      })

      // Get featured content
      .addCase(getFeaturedContent.pending, (state) => {
        state.posts.source = "featured";
        state.posts.data = [];
        state.posts.loading = true;
      })
      .addCase(getFeaturedContent.fulfilled, (state, action) => {
        // Drop the response if the user has since selected something else.
        if (state.posts.source !== "featured") return;
        state.posts.loading = false;
        state.posts.data = action.payload.data.data;
        state.posts.options = action.payload.data.options;
        state.posts.pagination = action.payload.data.pagination;
      })
      .addCase(getFeaturedContent.rejected, (state, action) => {
        state.posts.loading = false;
        state.posts.errors.push(action.payload as string);
      })

      // Get new content
      .addCase(getNewContent.pending, (state) => {
        state.posts.source = "new";
        state.posts.data = [];
        state.posts.loading = true;
      })
      .addCase(getNewContent.fulfilled, (state, action) => {
        if (state.posts.source !== "new") return;
        state.posts.loading = false;
        state.posts.data = action.payload.data.data;
        state.posts.options = action.payload.data.options;
        state.posts.pagination = action.payload.data.pagination;
      })
      .addCase(getNewContent.rejected, (state, action) => {
        state.posts.loading = false;
        state.posts.errors.push(action.payload as string);
      })

      // Get all content with Tag
      .addCase(getAllContentWithTag.pending, (state, action) => {
        state.posts.source = contentSelectionKey(action.meta.arg);
        state.posts.data = [];
        state.posts.loading = true;
      })
      .addCase(getAllContentWithTag.fulfilled, (state, action) => {
        if (state.posts.source !== contentSelectionKey(action.meta.arg)) return;
        state.posts.loading = false;
        state.posts.data = action.payload.data.data;
        state.posts.options = action.payload.data.options;
        state.posts.pagination = action.payload.data.pagination;
      })
      .addCase(getAllContentWithTag.rejected, (state, action) => {
        state.posts.loading = false;
        state.posts.errors.push(action.payload as string);
      })

      // Get other content
      .addCase(getOtherContent.pending, (state, action) => {
        const contentType = action.meta.arg.contentType;
        if (contentType === "Events") {
          state.events.loading = true;
        } else if (contentType === "People") {
          state.peoples.loading = true;
        } else if (contentType === "Tools") {
          state.tools.loading = true;
        } else if (contentType === "Support") {
          state.supports.loading = true;
        }
      })
      .addCase(getOtherContent.fulfilled, (state, action) => {
        const contentType = action.meta.arg.contentType;
        if (contentType === "Events") {
          const pageNumber = action.meta.arg.pageNumber;
          if (pageNumber === 1) {
            state.events.loading = false;
          } else {
            state.events.waiting = false;
          }
          state.events.data = action.payload.data.data;
          state.peoples.data = []
          state.tools.data = [];
          state.supports.data = [];
          state.events.options = action.payload.data.options;
          state.events.pagination = action.payload.data.pagination;
        } else if (contentType === "People") {
          const pageNumber = action.meta.arg.pageNumber;
          if (pageNumber === 1) {
            state.peoples.loading = false;
          } else {
            state.peoples.waiting = false;
          }
          state.peoples.data = action.payload.data.data;
          state.events.data = [];
          state.tools.data = [];
          state.supports.data = [];
          state.peoples.options = action.payload.data.options;
          state.peoples.pagination = action.payload.data.pagination;
        } else if (contentType === "Tools") {
          const pageNumber = action.meta.arg.pageNumber;
          if (pageNumber === 1) {
            state.tools.loading = false;
          } else {
            state.tools.waiting = false;
          }
          state.tools.data = action.payload.data.data;
          state.events.data = [];
          state.peoples.data = [];
          state.supports.data = [];
          state.tools.options = action.payload.data.options;
          state.tools.pagination = action.payload.data.pagination;
        } else if (contentType === "Support") {
          const pageNumber = action.meta.arg.pageNumber;
          if (pageNumber === 1) {
            state.supports.loading = false;
          } else {
            state.supports.waiting = false;
          }
          state.supports.data = action.payload.data.data;
          state.events.data = [];
          state.peoples.data = [];
          state.tools.data = [];
          state.supports.options = action.payload.data.options;
          state.supports.pagination = action.payload.data.pagination;
        }
      })
      .addCase(getOtherContent.rejected, (state, action) => {
        const contentType = action.meta.arg.contentType;
        if (contentType === "Events") {
          state.events.loading = false;
          state.events.errors.push(action.payload as string);
        } else if (contentType === "People") {
          state.peoples.loading = false;
          state.peoples.errors.push(action.payload as string);
        } else if (contentType === "Tools") {
          state.tools.loading = false;
          state.tools.errors.push(action.payload as string);
        } else if (contentType === "Support") {
          state.supports.loading = false;
          state.supports.errors.push(action.payload as string);
        }
      })

      // Get topic metadata
      .addCase(getTopicMetadata.pending, (state) => {
        state.topic.loading = true;
      })
      .addCase(getTopicMetadata.fulfilled, (state, action) => {
        state.topic.loading = false;
        state.topic.data = action.payload.data;
      })
      .addCase(getTopicMetadata.rejected, (state, action) => {
        state.topic.loading = false;
        state.topic.errors.push(action.payload as string);
      })

      // Create or Update review rating
      .addCase(submitReviewRatingForContent.pending, (state) => { })
      .addCase(submitReviewRatingForContent.fulfilled, (state, action) => {
        // POST /api/review returns the recomputed average and count, so write those
        // straight in. The previous version did `reviews += 1` per object type,
        // which over-counted when a user *changed* an existing rating, and left the
        // average untouched (that line was commented out) — so the card kept showing
        // the pre-rating number until the ~24h list cache expired.
        applyRatingAggregate(
          state,
          action.meta.arg.objectId,
          action.payload?.data
        );
      })
      .addCase(submitReviewRatingForContent.rejected, (state, action) => { });

  },
});

export default contentSlice.reducer;
